// pages/ReceivedEndowment.jsx
import React, { useState, useEffect } from 'react';
import { Container, Card, Spinner, Alert, Breadcrumb, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

// Components
import ReceivedEndowmentHeader from '../components/received/ReceivedEndowmentHeader';
import AuthenticationModal from '../components/received/AuthenticationModal';
import AcknowledgmentModal from '../components/received/AcknowledgmentModal';
import PasswordChangeModal from '../components/received/PasswordChangeModal';
import ConfirmLogoutModal from '../components/received/ConfirmLogoutModal';
import DeclineConfirmationModal from '../components/received/DeclineConfirmationModal';

// View Modals
import ViewCardModal from '../components/modals/BankCardModals/ViewCardModal';
import ViewPasswordModal from '../components/modals/PasswordModals/ViewPasswordModal';
import FileViewerReceievedModal from '../components/modals/FileModals/FileViewerReceievedModal';
import ViewPhraseModal from '../components/modals/BlockchainModals/ViewPhraseModal';

import { viewBankCard } from '../services/bankcard.service';
import { viewPassword } from '../services/password.service';
import { viewPhrase } from '../services/blockchain.service';

// Hooks
import { useReceivedEndowment } from '../hooks/useReceivedEndowment';
import { useNavigation } from '../hooks/useNavigation';

// Constants
import { RECIPIENT_ENDPOINTS } from '../utils/constants';
import { STREAM_ENDPOINTS } from '../utils/constants';

import { useModals } from '../hooks/useModals';
import ItemsList from '../components/received/ItemsList';


// Styles
import '../styles/pages/receivedEndowment.css';

const ReceivedEndowment = () => {
  const navigate = useNavigate();

  // Custom hooks
  const {
    inheritances,
    activeInheritanceId,
    currentInheritance,
    isLoading,
    isAuthenticated,
    needsPasswordChange,
    alert,
    setAlert,
    selectInheritance,
    checkInheritanceStatus,
    handleAuthSuccess,
    setFilesCurrentPath,
    handlePasswordChangeSuccess,
    handleAcknowledgmentSuccess,
    handleAcknowledge,
    handleDecline,
    handleDownloadFile,
    handleLogout,
    showAlert
  } = useReceivedEndowment();

  const {
    viewFileModalOpen,
    viewPasswordModalOpen,
    viewCardModalOpen,
    viewBlockchainModalOpen,
    currentItem,
    openViewFileModalReceived,
    openViewPasswordModal,
    openViewCardModal,
    openViewBlockchainModal,
    closeViewFileModal,
    closeViewPasswordModal,
    closeViewCardModal,
    closeViewBlockchainModal,
  } = useModals();

  const {
  currentPath,
  filesCurrentPath, 
  handleBackClick,
  handleCategoryClick,
  handleFilesFolderClick, 
  handleFilesBackClick, 
  resetFolderNavigation,
  navigateTo
} = useNavigation();

  // Modal states
  const [showAuthModal, setShowAuthModal] = useState(!isAuthenticated);
  const [showAcknowledgmentModal, setShowAcknowledgmentModal] = useState(false);
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
  const [showConfirmLogoutModal, setShowConfirmLogoutModal] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);

  // View modal states
  const [showViewFileModal, setShowViewFileModal] = useState(false);
  const [showViewPasswordModal, setShowViewPasswordModal] = useState(false);
  const [showViewCardModal, setShowViewCardModal] = useState(false);
  const [showViewPhraseModal, setShowViewPhraseModal] = useState(false);


  // Inheritance selector collapsed state
  const [inheritanceSelectorCollapsed, setInheritanceSelectorCollapsed] = useState(false);

  // Sync authentication modal with authentication state
  useEffect(() => {
    setShowAuthModal(!isAuthenticated);
  }, [isAuthenticated]);

  // Sync password change modal with needsPasswordChange state
  useEffect(() => {
    setShowPasswordChangeModal(needsPasswordChange);
  }, [needsPasswordChange]);

  const handleModalDownload = () => {
    if (currentItem && (currentItem.fileName || currentItem.name)) {
      handleDownloadFile(currentItem.fileName || currentItem.name);
    }
  };

  // Handle acknowledge button click
  const handleAcknowledgeClick = () => {
    setShowAcknowledgmentModal(true);
  };

  // Handle decline button click with confirmation modal
  const handleDeclineClick = () => {
    setShowDeclineModal(true);
  };

  // Handle decline confirmation from modal
  const handleDeclineConfirm = async (confirmationText) => {
  const expectedText = 'I understand that I will lose access to this Endowment';

  if (confirmationText !== expectedText) {
    showAlert('Confirmation text did not match. Please try again.', 'danger');
    return;
  }

  const success = await handleDecline(confirmationText);
  if (success) {
    setShowDeclineModal(false);
    
    // Check if this was their only endowment
    if (inheritances.length === 1) {
      // This was their only endowment, log them out
      setTimeout(() => {
        handleLogout();
        navigate('/login');
      }, 1500); // Give them a moment to see any success message
    }
  }
};

  const getContentTypeFromFileName = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();

    const mimeTypes = {
      // Images
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'bmp': 'image/bmp',
      'svg': 'image/svg+xml',

      // Documents
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'ppt': 'application/vnd.ms-powerpoint',
      'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',

      // Text
      'txt': 'text/plain',
      'html': 'text/html',
      'css': 'text/css',
      'js': 'text/javascript',
      'json': 'application/json',
      'xml': 'text/xml',

      // Audio
      'mp3': 'audio/mpeg',
      'wav': 'audio/wav',
      'ogg': 'audio/ogg',
      'flac': 'audio/flac',

      // Video
      'mp4': 'video/mp4',
      'avi': 'video/avi',
      'mov': 'video/quicktime',
      'wmv': 'video/x-ms-wmv',
      'mkv': 'video/x-matroska',

      // Archives
      'zip': 'application/zip',
      'rar': 'application/x-rar-compressed',
      '7z': 'application/x-7z-compressed'
    };

    return mimeTypes[extension] || 'application/octet-stream';
  };


  const handleViewItem = async (itemName, itemType) => {
  try {
    // Determine bucket based on itemType
    let bucket;
    switch (itemType.toLowerCase()) {
      case 'files':
        bucket = 'files';
        break;
      case 'passwords':
        bucket = 'passwords';
        break;
      case 'banking':
        bucket = 'bankcards';
        break;
      case 'blockchain':
        bucket = 'blockchain';
        break;
      default:
        throw new Error(`Invalid item type: ${itemType}`);
    }

    // Get inheritance token
    let inheritanceToken;
    if (currentInheritance && currentInheritance.token) {
      inheritanceToken = currentInheritance.token;
    } else {
      inheritanceToken = sessionStorage.getItem(`token_${activeInheritanceId}`) ||
        sessionStorage.getItem('token');
    }

    if (itemType.toLowerCase() === 'files') {
      // For files: use the full path (itemName already contains full path)
      
      const response = await fetch(STREAM_ENDPOINTS.FILE, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${inheritanceToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bucket: bucket,
          fileName: itemName, // This is already the full path
          inheritanceId: activeInheritanceId
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const contentType = response.headers.get('Content-Type') || getContentTypeFromFileName(itemName);
      const url = URL.createObjectURL(blob);

      let textContent = null;
      if (contentType.startsWith('text/')) {
        textContent = await blob.text();
      }

      // Extract just the filename for display
      const displayName = itemName.split('/').pop();

      const itemWithData = {
        fileName: itemName, // Full path for server
        name: displayName, // Display name
        originalName: itemName,
        type: itemType.toLowerCase(),
        bucket: bucket,
        contentType: contentType,
        url: url,
        blob: blob,
        textContent: textContent
      };

      openViewFileModalReceived(itemWithData);
    } else {
      // For passwords, banking, blockchain — decrypt (these don't have folder paths)
      let decryptedData;
      switch (itemType.toLowerCase()) {
        case 'passwords':
          decryptedData = await viewPassword(itemName, inheritanceToken);
          break;
        case 'banking':
          decryptedData = await viewBankCard(itemName, inheritanceToken);
          break;
        case 'blockchain':
          decryptedData = await viewPhrase(itemName, inheritanceToken);
          break;
      }

      const itemWithData = {
        name: itemName,
        data: decryptedData,
        type: itemType.toLowerCase(),
        originalName: itemName,
        ...decryptedData
      };

      switch (itemType.toLowerCase()) {
        case 'passwords':
          openViewPasswordModal(itemWithData);
          break;
        case 'banking':
          openViewCardModal(itemWithData);
          break;
        case 'blockchain':
          openViewBlockchainModal(itemWithData);
          break;
      }
    }
  } catch (error) {
    console.error('Error viewing item:', error);
    showAlert(`Error viewing item: ${error.message}`, 'danger');
    throw error;
  }
};
  // Handle item click (view file, password, banking, blockchain item)
  const handleItemClick = async (itemName, itemType) => {
    try {
      const itemData = await handleViewItem(itemName, itemType);

      // Show the appropriate modal
      switch (itemType.toLowerCase()) {
        case 'files':
          // For files, we need to handle different content types
          setShowViewFileModal(true);
          break;
        case 'passwords':
          setShowViewPasswordModal(true);
          break;
        case 'banking':
          setShowViewCardModal(true);
          break;
        case 'blockchain':
          setShowViewPhraseModal(true);
          break;
      }
    } catch (error) {
      // Error already handled in hook
    }
  };

  // Handle authentication modal success
  const onAuthSuccess = (data) => {
    handleAuthSuccess(data);
    // Don't manually set showAuthModal to false here - let useEffect handle it
  };

  // Handle password change modal success
  const onPasswordChangeSuccess = () => {
    handlePasswordChangeSuccess();
    // Don't manually set showPasswordChangeModal to false here - let useEffect handle it
  };

  // Handle acknowledgment modal success
  const onAcknowledgmentSuccess = () => {
    handleAcknowledgmentSuccess();
    setShowAcknowledgmentModal(false);
  };

  // Handle logout confirmation
  const onLogoutConfirm = () => {
    handleLogout();
    setShowConfirmLogoutModal(false);
    navigate('/login');
  };

  // Remove .txt extension helper
  const removeTxtExtension = (name) => {
    if (name && name.endsWith('.txt')) {
      return name.slice(0, -4);
    }
    return name;
  };

  // Render inheritance status and action buttons
  const renderInheritanceStatus = () => {
    if (!currentInheritance) return null;

    const { isAvailable, statusType } = checkInheritanceStatus(currentInheritance);

    switch (statusType) {
      case 'declined':
        return (
          <Alert variant="danger" className="re-status-alert">
            <div className="d-flex align-items-center">
              <i className="fas fa-times-circle re-status-icon me-3"></i>
              <div>
                <h5 className="mb-1">Endowment Declined</h5>
                <p className="mb-0">You have declined this Endowment and no longer have access to its contents.</p>
                <p className="mb-0">If this was done in error, please contact the creator.</p>
              </div>
            </div>
          </Alert>
        );

      case 'expired':
        return (
          <Alert variant="danger" className="re-status-alert">
            <div className="d-flex align-items-center">
              <i className="fas fa-calendar-times re-status-icon me-3"></i>
              <div>
                <h5 className="mb-1">Endowment Expired</h5>
                <p className="mb-0">This Endowment has expired and is no longer available.</p>
                <p className="mb-0">The expiration date was {new Date(currentInheritance.expiration_date).toLocaleDateString()}.</p>
              </div>
            </div>
          </Alert>
        );

      case 'not_released':
        return (
          <Alert variant="info" className="re-status-alert">
            <div className="d-flex align-items-center">
              <i className="fas fa-clock re-status-icon me-3"></i>
              <div>
                <h5 className="mb-1">Endowment Not Yet Released</h5>
                <p className="mb-0">This Endowment will be available on {new Date(currentInheritance.release_date).toLocaleDateString('en-US', {
                          timeZone: 'UTC'
                        })}</p>
                <p className="mb-0">You will receive an email notification when it becomes available.</p>
              </div>
            </div>
          </Alert>
        );

      case 'pending_acknowledgment':
        return (
          <Alert variant="warning" className="re-status-alert">
            <div className="d-flex align-items-center">
              <i className="fas fa-exclamation-triangle re-status-icon me-3"></i>
              <div className="flex-grow-1">
                <h5 className="mb-2">Action Required</h5>
                <p className="mb-3">Please acknowledge or decline this Endowment from {currentInheritance.creator_email}.</p>
                <div className="d-flex gap-2">
                  <Button
                    variant="success"
                    size="sm"
                    onClick={handleAcknowledgeClick}
                    disabled={isLoading}
                  >
                    <i className="fas fa-check-circle me-2"></i>
                    Accept Endowment
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={handleDeclineClick}
                    disabled={isLoading}
                  >
                    <i className="fas fa-times-circle me-2"></i>
                    Decline Endowment
                  </Button>
                </div>
              </div>
            </div>
          </Alert>
        );

      case 'available':
      default:
        return null; // No status message needed for available endowments
    }
  };

  const handleContentBackClick = () => {
  const isInFiles = currentPath.length > 0 && currentPath[currentPath.length - 1].toLowerCase() === 'files';
  
  if (isInFiles && filesCurrentPath.length > 0) {
    handleFilesBackClick();
  } else {
    handleBackClick();
  }
};

  // Render category grid
  const renderCategoryGrid = () => {
    const categories = [
      { name: 'Files', icon: 'fa-file-alt', color: '#000000', description: 'Access shared documents and files' },
      { name: 'Passwords', icon: 'fa-key', color: '#FFD700', description: 'View stored login credentials' },
      { name: 'Banking', icon: 'fa-credit-card', color: '#00CC5C', description: 'Access banking and payment information' },
      { name: 'Blockchain', icon: 'fa-link', color: '#4B0082', description: 'View blockchain wallet information' }
    ];

    return (
      <div className="re-category-container">
        <h4 className="re-section-title">Endowment Categories</h4>
        <div className="row">
          {categories.map((category, index) => (
            <div className="col-md-3 col-sm-6 mb-4" key={index}>
              <div
                className="re-category-tile"
                onClick={() => navigateTo([category.name])}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    navigateTo([category.name]);
                  }
                }}
              >
                <div
                  className="re-category-icon-wrapper"
                  style={{ backgroundColor: `${category.color}15` }}
                >
                  <i
                    className={`fas ${category.icon} re-category-icon`}
                    style={{ color: category.color }}
                  ></i>
                </div>
                <div className="re-category-details">
                  <div className="re-category-name">{category.name}</div>
                  <div className="re-category-description">{category.description}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render items list for a category
  const renderItemsList = () => {
  return (
    <ItemsList
      currentPath={currentPath}
      filesCurrentPath={filesCurrentPath}
      inheritance={currentInheritance}
      onItemClick={handleItemClick}
      onDownloadFile={handleDownloadFile}
      onFolderClick={handleFilesFolderClick}
      onBackClick={handleFilesBackClick}
    />
  );
};

  return (
    <Container fluid className="re-container">
      {/* Header */}
      <div className="re-app-header">
        <div className="re-app-logo">
          <img src="/img/logo.png" alt="PhēnixLoc" onError={(e) => { e.target.style.display = 'none' }} />
          <h3>PhēnixLoc</h3>
        </div>
        <button className="re-logout-btn" onClick={() => setShowConfirmLogoutModal(true)}>
          <i className="fas fa-sign-out-alt"></i> Logout
        </button>
      </div>

      {/* Inheritance Selector (shown when multiple inheritances) OR Single Inheritance with Pending Status */}
      {(inheritances.length >= 1) && (
        <div className="re-inheritance-selector mb-4">
          <div className="re-inheritance-selector-header">
            <h3>{inheritances.length > 1 ? 'Your Endowments' : 'Your Endowment'}</h3>
            {inheritances.length > 1 && (
              <button
                className="re-toggle-btn"
                onClick={() => setInheritanceSelectorCollapsed(!inheritanceSelectorCollapsed)}
                title={inheritanceSelectorCollapsed ? "Expand" : "Collapse"}
              >
                <i className={`fas ${inheritanceSelectorCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'}`}></i>
              </button>
            )}
          </div>

          {(!inheritanceSelectorCollapsed || inheritances.length === 1) && (
            <div className="re-inheritance-list">
              {inheritances.map((inheritance, index) => {
                const { isAvailable, statusType } = checkInheritanceStatus(inheritance);
                const isPending = statusType === 'pending_acknowledgment';

                return (
                  <div
                    key={index}
                    className={`re-inheritance-item ${activeInheritanceId === inheritance.id ? 'active' : ''}`}
                  >
                    <div
                      className="re-inheritance-content"
                      onClick={() => selectInheritance(inheritance)}
                    >
                      <div>
                        <h5 className="mb-1">{removeTxtExtension(inheritance.filename)}</h5>
                        <p className="mb-1">From: {inheritance.creator_email}</p>
                        <small>Release Date: {new Date(inheritance.release_date).toLocaleDateString('en-US', {
                          timeZone: 'UTC'
                        })}</small>
                        {isPending && (
                          <div className="mt-2">
                            <small className="text-warning">
                              <i className="fas fa-exclamation-triangle me-1"></i>
                              Action required - Please accept or decline this endowment
                            </small>
                          </div>
                        )}
                      </div>
                      <div className="re-inheritance-status">
                        <span className={`re-badge ${inheritance.is_accepted ? 're-badge-success' :
                          inheritance.is_declined ? 're-badge-danger' : 're-badge-warning'
                          }`}>
                          <i className={`fas ${inheritance.is_accepted ? 'fa-check-circle' :
                            inheritance.is_declined ? 'fa-times-circle' : 'fa-clock'
                            }`}></i>
                          {inheritance.is_accepted ? 'Acknowledged' :
                            inheritance.is_declined ? 'Declined' : 'Pending'}
                        </span>
                      </div>
                    </div>

                    {/* Quick Action Buttons for Pending Inheritances - Always show for debugging */}
                    {!inheritance.is_declined && (
                      <div className="re-quick-actions">
                        {!inheritance.is_accepted && (
                          <Button
                            variant="success"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (activeInheritanceId !== inheritance.id) {
                                selectInheritance(inheritance);
                              }
                              setTimeout(() => setShowAcknowledgmentModal(true), activeInheritanceId !== inheritance.id ? 200 : 0);
                            }}
                            disabled={isLoading}
                          >
                            <i className="fas fa-check me-1"></i>
                            Accept Endowment
                          </Button>
                        )}
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (activeInheritanceId !== inheritance.id) {
                              selectInheritance(inheritance);
                              setTimeout(() => setShowDeclineModal(true), 200);
                            } else {
                              setShowDeclineModal(true);
                            }
                          }}
                          disabled={isLoading}
                        >
                          <i className="fas fa-times me-1"></i>
                          {inheritance.is_accepted ? 'Decline Endowment' : 'Decline Endowment'}
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Inheritance Header */}
      {currentInheritance && (
        <ReceivedEndowmentHeader
          inheritance={currentInheritance}
        />
      )}

      {/* Status Alert */}
      {currentInheritance && renderInheritanceStatus()}

      {/* Breadcrumb */}
{currentInheritance && (currentPath.length > 0 || filesCurrentPath.length > 0) && (
  <Breadcrumb className="re-breadcrumb">
    <Breadcrumb.Item
      onClick={() => {
        navigateTo([]);
        resetFolderNavigation();
      }}
      linkAs="span"
      className="re-breadcrumb-item-clickable"
    >
      Home
    </Breadcrumb.Item>
    
    {currentPath.map((item, index) => (
      <Breadcrumb.Item
        key={index}
        active={index === currentPath.length - 1 && filesCurrentPath.length === 0}
        onClick={() => {
          if (index !== currentPath.length - 1 || filesCurrentPath.length > 0) {
            navigateTo(currentPath.slice(0, index + 1));
            resetFolderNavigation();
          }
        }}
        linkAs="span"
        className={index !== currentPath.length - 1 || filesCurrentPath.length > 0 ? 're-breadcrumb-item-clickable' : ''}
      >
        {item}
      </Breadcrumb.Item>
    ))}
    
    {/* Files folder breadcrumbs */}
    {currentPath.length > 0 && currentPath[currentPath.length - 1].toLowerCase() === 'files' && 
     filesCurrentPath.map((folder, index) => (
      <Breadcrumb.Item
        key={`folder-${index}`}
        active={index === filesCurrentPath.length - 1}
        onClick={() => index !== filesCurrentPath.length - 1 && setFilesCurrentPath(prev => prev.slice(0, index + 1))}
        linkAs="span"
        className={index !== filesCurrentPath.length - 1 ? 're-breadcrumb-item-clickable' : ''}
      >
        {folder}
      </Breadcrumb.Item>
    ))}
  </Breadcrumb>
)}

      {/* Main Content - Only show if inheritance is available or pending acknowledgment */}
      {currentInheritance && (() => {
        const { isAvailable, statusType } = checkInheritanceStatus(currentInheritance);

        // Only show content card for available or pending acknowledgment inheritances
        if (statusType === 'available' || statusType === 'pending_acknowledgment') {
          return (
            <Card className="re-content-card">
              <Card.Body>
                {isLoading ? (
                  <div className="re-loading-container">
                    <Spinner animation="border" variant="primary" />
                  </div>
                ) : (
                  (() => {
                    // Only show content if available and acknowledged
                    if (isAvailable && statusType === 'available') {
                      return currentPath.length === 0 ? renderCategoryGrid() : renderItemsList();
                    } else {
                      // Show message for pending acknowledgment
                      return (
                        <div className="re-empty-state">
                          <i className="fas fa-clock re-empty-icon"></i>
                          <h3>Endowment Pending Acknowledgment</h3>
                          <p>Please accept or decline this endowment using the buttons above to access its contents.</p>
                        </div>
                      );
                    }
                  })()
                )}
              </Card.Body>
            </Card>
          );
        }

        // For declined, expired, or not-released inheritances, don't show the content card at all
        return null;
      })()}

      {/* Alerts */}
      {alert.show && (
        <div className={`re-alert re-alert-${alert.variant}`}>
          <div className="re-alert-content">
            <span>{alert.message}</span>
            <button className="re-alert-close" onClick={() => setAlert(prev => ({ ...prev, show: false }))}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      <AuthenticationModal
        show={showAuthModal}
        onSuccess={onAuthSuccess}
        onError={(message) => showAlert(message, 'danger')}
        endpoints={RECIPIENT_ENDPOINTS}
      />

      <AcknowledgmentModal
        show={showAcknowledgmentModal}
        onSuccess={onAcknowledgmentSuccess}
        onError={(message) => showAlert(message, 'danger')}
        inheritance={currentInheritance}
        endpoints={RECIPIENT_ENDPOINTS}
      />

      <PasswordChangeModal
        show={showPasswordChangeModal}
        onSuccess={onPasswordChangeSuccess}
        onError={(message) => showAlert(message, 'danger')}
        endpoints={RECIPIENT_ENDPOINTS}
      />

      <ConfirmLogoutModal
        show={showConfirmLogoutModal}
        onConfirm={onLogoutConfirm}
        onCancel={() => setShowConfirmLogoutModal(false)}
      />

      <DeclineConfirmationModal
        show={showDeclineModal}
        onHide={() => setShowDeclineModal(false)}
        onConfirm={handleDeclineConfirm}
        isLoading={isLoading}
        inheritanceName={currentInheritance ? removeTxtExtension(currentInheritance.filename) : ''}
      />

      {/* View Modals */}
      {currentItem && (
        <>
          {/* View Modals */}
          <FileViewerReceievedModal
            show={viewFileModalOpen}
            onHide={closeViewFileModal}
            fileData={currentItem}
            onDownload={handleModalDownload}
          />
          <ViewPasswordModal
            show={viewPasswordModalOpen}
            onHide={closeViewPasswordModal}
            passwordData={currentItem}
          />

          <ViewCardModal
            show={viewCardModalOpen}
            onHide={closeViewCardModal}
            cardData={currentItem}
          />

          <ViewPhraseModal
            show={viewBlockchainModalOpen}
            onHide={closeViewBlockchainModal}
            phraseData={currentItem}
          />
        </>
      )}
    </Container>
  );
};

export default ReceivedEndowment;